// Minimal support-service API client. Lives outside services/apiService.ts to
// keep the issue reporter self-contained — easy to remove/relocate later.

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  if (typeof window === 'undefined') return 'dev';
  const h = window.location.hostname;
  if (h.startsWith('dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.startsWith('stage.') || h.includes('staging')) return 'staging';
  return 'prod';
};

const baseDomain = (): string => {
  if (typeof window === 'undefined') return 'cafemenumaster.com';
  const h = window.location.hostname;
  // Drop dev./stage./www. prefix to get the apex
  return h.replace(/^(dev|stage|www|admin|admin\.dev|admin\.stage)\./, '');
};

const apiHost = (): string => {
  // Allow Vite override
  try {
    const fromEnv = (import.meta as any)?.env?.VITE_ISSUES_API_BASE;
    if (fromEnv) return fromEnv;
  } catch {
    // ignore
  }
  // On localhost dev, route through the Vite proxy (`/issues-api` → dev backend)
  // so we don't try to resolve `issues.api.dev.localhost`.
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return '/issues-api';
  }
  const stage = detectStage();
  const apex = baseDomain();
  if (stage === 'prod') return `https://issues.api.${apex}`;
  if (stage === 'staging') return `https://issues.api.stage.${apex}`;
  return `https://issues.api.dev.${apex}`;
};

function getAuthHeader(): Record<string, string> {
  const token =
    (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

function getTenantHeader(): Record<string, string> {
  const t =
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('activeTenantId')) ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('activeTenantId')) ||
    '';
  return t ? { 'x-tenant-id': t } : {};
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiHost()}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...getTenantHeader(),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`support API ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export interface CreateIssuePayload {
  title: string;
  description: string;
  category: string;
  priority: 'minor' | 'annoying' | 'blocking';
  context: Record<string, any>;
  consoleErrors?: any[];
  networkFailures?: any[];
  pageState?: string;
  screenshotKey?: string;
  notifyByEmail?: boolean;
}

// The admin panel authenticates against the PlatformAdmin Cognito pool, so its
// reporter calls must hit the admin-pool-authorized routes (/admin/report/*).
// The regular /issues/* routes only accept the regular user pool and would 401.
export const supportApi = {
  createIssue: (payload: CreateIssuePayload) =>
    call<{ issue: any; ticketNumber: string }>('/admin/report/issues', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listMyIssues: (status?: string) =>
    call<{ issues: any[]; count: number }>(
      `/admin/report/issues/my${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),

  getIssue: (id: string) =>
    call<{ issue: any; comments: any[] }>(`/issues/${encodeURIComponent(id)}`),

  addComment: (id: string, body: string) =>
    call<{ comment: any }>(`/issues/${encodeURIComponent(id)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  confirmFix: (id: string, note?: string) =>
    call<{ status: string }>(`/issues/${encodeURIComponent(id)}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  rejectFix: (id: string, note: string) =>
    call<{ status: string }>(`/issues/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  getScreenshotUploadUrl: (contentLengthBytes: number, contentType = 'image/png') =>
    call<{ uploadUrl: string; key: string; expiresInSeconds: number }>(
      '/admin/report/issues/screenshot-upload-url',
      { method: 'POST', body: JSON.stringify({ contentType, contentLengthBytes }) },
    ),

  uploadScreenshot: async (blob: Blob): Promise<string | undefined> => {
    try {
      const presign = await supportApi.getScreenshotUploadUrl(blob.size);
      if (!presign?.uploadUrl || !presign?.key) {
        console.warn('[issueReporter] presign response missing fields', presign);
        return undefined;
      }
      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/png' },
        body: blob,
      });
      if (!putRes.ok) {
        console.warn('[issueReporter] screenshot upload failed', putRes.status);
        return undefined;
      }
      return presign.key;
    } catch (e) {
      console.warn('[issueReporter] screenshot upload error', e);
      return undefined;
    }
  },
};
